import { useEffect, useState } from 'react';
import { getEmailEvents, EmailEventsResponse } from '../utils/api';

interface ActivityDetailModalProps {
  emailId: string;
  onClose: () => void;
}

type DeviceType = 'mobile' | 'tablet' | 'desktop';

function parseDeviceType(ua: string | null): DeviceType {
  if (!ua) return 'desktop';
  const lower = ua.toLowerCase();
  if (lower.includes('iphone') || (lower.includes('android') && lower.includes('mobile'))) {
    return 'mobile';
  }
  if (lower.includes('ipad') || lower.includes('tablet') || (lower.includes('android') && !lower.includes('mobile'))) {
    return 'tablet';
  }
  return 'desktop';
}

function DeviceIcon({ type }: { type: DeviceType }) {
  if (type === 'mobile') {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
        <line x1="12" y1="18" x2="12.01" y2="18" />
      </svg>
    );
  }
  if (type === 'tablet') {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
        <line x1="12" y1="18" x2="12.01" y2="18" />
      </svg>
    );
  }
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  );
}

function LocationIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function GmailIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  );
}

function ClickIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 9l-2 2 2 2" />
      <path d="M15 15l2-2-2-2" />
      <path d="M3 12a9 9 0 1 0 18 0 9 9 0 0 0-18 0" />
      <path d="M11 9l4 6" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function buildGmailUrl(messageId: string): string {
  // Gmail search with rfc822msgid: format
  const encodedId = encodeURIComponent(`rfc822msgid:${messageId}`);
  return `https://mail.google.com/mail/u/0/#search/${encodedId}`;
}

function formatProviderHint(hint: string | null): string {
  if (!hint) return 'Unknown';
  const providers: Record<string, string> = {
    'gmail_image_proxy': 'Gmail',
    'outlook_proxy': 'Outlook',
    'yahoo_proxy': 'Yahoo',
    'apple_proxy': 'Apple Mail',
  };
  return providers[hint] || hint;
}

function formatLocation(city: string | null, country: string | null): string {
  if (city && country) return `${city}, ${country}`;
  if (country) return country;
  return 'Unknown';
}

function formatEventTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }) + ' at ' + date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export function ActivityDetailModal({ emailId, onClose }: ActivityDetailModalProps) {
  const [data, setData] = useState<EmailEventsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await getEmailEvents(emailId);
        setData(result);
      } catch (err) {
        setError('Failed to load activity details');
        console.error('Failed to fetch email events:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [emailId]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3>Activity Timeline</h3>
            {data && (
              <p className="modal-subtitle">
                {data.email.to_email || 'Unknown recipient'} — {data.email.subject || 'No subject'}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: 'var(--gray-400)',
            }}
          >
            ×
          </button>
        </div>

        <div className="modal-body">
          {loading && (
            <div className="empty-state">Loading...</div>
          )}

          {error && (
            <div className="empty-state" style={{ color: 'var(--danger)' }}>
              {error}
            </div>
          )}

          {data && data.events.length === 0 && (
            <div className="empty-state">
              <p>No events recorded yet</p>
            </div>
          )}

          {data && data.events.length > 0 && (
            <div className="timeline-list">
              {data.events.map((event, index) => {
                const deviceType = parseDeviceType(event.raw_ua);
                const isLast = index === data.events.length - 1;
                const isClick = event.type === 'click';
                return (
                  <div key={event.id} className={`timeline-item ${isLast ? 'timeline-item-last' : ''}`}>
                    <div
                      className="timeline-dot"
                      style={isClick ? { background: '#ffc107' } : undefined}
                    />
                    <div className="timeline-content">
                      <div className="timeline-time" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {isClick ? <ClickIcon /> : <EyeIcon />}
                        <span>{formatEventTime(event.created_at)}</span>
                        {isClick && (
                          <span className="badge badge-warning" style={{ background: '#ffc107', color: '#000', fontSize: '10px' }}>
                            Click
                          </span>
                        )}
                      </div>
                      {isClick && event.link_url && (
                        <div style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '4px', wordBreak: 'break-all' }}>
                          {event.link_url.length > 60 ? event.link_url.substring(0, 60) + '...' : event.link_url}
                        </div>
                      )}
                      <div className="timeline-details">
                        <span className="timeline-detail">
                          <LocationIcon />
                          {formatLocation(event.city, event.country)}
                        </span>
                        <span className="timeline-detail">
                          <DeviceIcon type={deviceType} />
                          {deviceType.charAt(0).toUpperCase() + deviceType.slice(1)}
                        </span>
                        <span className="badge badge-info">
                          {formatProviderHint(event.provider_hint)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <span className="modal-footer-info">
            {data && (
              <>
                {data.email.open_count} open{data.email.open_count !== 1 ? 's' : ''}
                {data.email.click_count > 0 && (
                  <> · {data.email.click_count} click{data.email.click_count !== 1 ? 's' : ''}</>
                )}
                {data.email.link_count > 0 && (
                  <> · {data.email.link_count} link{data.email.link_count !== 1 ? 's' : ''} tracked</>
                )}
              </>
            )}
          </span>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {data?.email.message_id && (
              <a
                href={buildGmailUrl(data.email.message_id)}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', textDecoration: 'none' }}
              >
                <GmailIcon />
                View in Gmail
              </a>
            )}
            <button className="btn btn-primary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
